import {
  IonAlert,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonPage,
  IonRow,
} from '@ionic/react';
import { useState, useEffect, useRef } from 'react';
import ProgressIndicator from '../../components/progress-indicator/progress-indicator';
import { useIcons } from '../../hooks/useIcons';
import Flatten from './flatten';
import style from './folder-flattener.module.scss';
import Folders, { FolderData } from './folders';
import Verify from './verify';

export type FlattenedFolders = { current_path: string; new_path: string };

export interface FileOperationProgress {
  completedCount: number;
  totalFilesRequested: number;
  percentage: number;
  currentFile?: string;
  status?: 'started' | 'complete';
}

export default function FolderFlattener() {
  const { icons } = useIcons();

  const steps = [
    {
      label: 'Select',
      callback: () => setCurrentStep(1),
    },
    {
      label: 'Backup',
      callback: () => {
        setCurrentStep(2);
        setShowBackupModal(true);
      },
    },
    {
      label: 'Destination',
      callback: () => {
        setCurrentStep(3);
        setShowBackupModal(false);
      },
    },
    {
      label: 'Verify',
      callback: () => setCurrentStep(4),
    },
    {
      label: 'Flatten',
      callback: () => setCurrentStep(5),
    },
  ];

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFolders, setSelectedFolders] = useState<FolderData[]>([]);
  const [destination, setDestination] = useState<FolderData>();
  const [showBackupModal, setShowBackupModal] = useState<boolean>(false);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isReloadingFolders, setIsReloadingFolders] = useState<boolean>(false);
  const [isMovingFiles, setIsMovingFiles] = useState<boolean>(false);
  const [showCancelAlert, setShowCancelAlert] = useState<boolean>(false);
  const [flattenMap, setFlattenMap] = useState<FlattenedFolders[]>([]);
  const [fileOperationResponse, setFileOperationResponse] = useState<any>(null);
  const [operationProgress, setOperationProgress] =
    useState<FileOperationProgress | null>(null);
  const progressListenerRef = useRef<
    ((progress: FileOperationProgress) => void) | null
  >(null);

  useEffect(() => {
    return () => {
      if (progressListenerRef.current) {
        window.api?.removeListener?.(
          'file-operation-progress',
          progressListenerRef.current,
        );
      }
    };
  }, []);

  const handleSelectFolder = async (data: FolderData[]) => {
    setSelectedFolders(data);
    setCurrentStep(2);
    setShowBackupModal(true);
  };

  const handleBackupLibrary = async () => {
    setCurrentStep(3);
    setShowBackupModal(false);
  };

  const onCloseBackupModal = async () => {
    setShowBackupModal(false);
  };

  const setDestinationButton = async () => {
    const openDialog = (response: FolderData[]) => {
      if (!response || response.length === 0) return;

      const selectedPath = response[0].path;
      const isSourceFolder = selectedFolders.some(
        (folder) => folder.path === selectedPath,
      );

      if (isSourceFolder) {
        setErrorMessage(
          'The destination folder cannot be the same as any of the source folders. Please select a different folder.',
        );
        setShowErrorAlert(true);
        return;
      }

      setDestination(response[0]);
      setCurrentStep(4);
    };

    window.api?.once('open-directory-dialog', openDialog);
    window.api?.send('open-directory-dialog', {
      options: ['openDirectory', 'createDirectory', 'promptToCreate'],
    });
  };

  const handleVerify = async (data: FlattenedFolders[]) => {
    setFlattenMap(data);
    if (isReloadingFolders) return;

    setIsMovingFiles(true);
    setOperationProgress({
      completedCount: 0,
      totalFilesRequested: data.length,
      percentage: 0,
      status: 'started',
    });
    const onProgress = (progress: FileOperationProgress) => {
      setOperationProgress(progress);
    };
    progressListenerRef.current = onProgress;
    window.api?.on?.('file-operation-progress', onProgress);

    window.api?.send('file-operation', {
      action: 'copy',
      destination: destination?.path,
      items: data,
    });
    window.api?.once('file-operation', (response: any) => {
      if (progressListenerRef.current) {
        window.api?.removeListener?.(
          'file-operation-progress',
          progressListenerRef.current,
        );
        progressListenerRef.current = null;
      }

      setFileOperationResponse(response);
      setIsMovingFiles(false);
      setOperationProgress(null);
      setCurrentStep(5);
    });
  };

  const handleFinish = () => {
    setCurrentStep(1);
    setSelectedFolders([]);
    setDestination(undefined);
    setFlattenMap([]);
    setFileOperationResponse(null);
  };

  const handleCancelClick = () => {
    setShowCancelAlert(true);
  };

  const confirmCancel = () => {
    window.api?.send?.('cancel-file-operation', null);
    setShowCancelAlert(false);
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <IonGrid>
          <IonRow>
            <IonCol size="12">
              <div className={style['page-header']}>
                <h1 className={style['title']} style={{ textAlign: 'left' }}>
                  <IonIcon
                    icon={icons.folderFlattener}
                    style={{ marginRight: '10px' }}
                  />{' '}
                  Folder Flattener
                </h1>
              </div>
            </IonCol>
            <IonCol size="12" className={style['step-header-col']}>
              <div className={style['step-header']}>
                <ProgressIndicator steps={steps} currentStep={currentStep} />
              </div>
            </IonCol>
          </IonRow>

          <IonRow>
            <IonCol style={{ padding: '0 10px' }}>
              <div className={`${style['flatten-path']}`}>
                {(currentStep === 1 || currentStep === 2) && (
                  <Folders callback={handleSelectFolder} />
                )}

                {currentStep === 2 && (
                  <IonAlert
                    isOpen={showBackupModal}
                    onDidDismiss={onCloseBackupModal}
                    cssClass="backup-confirmation"
                    header="Did you back up your folders?"
                    message="Please back up these files and folders before flattening."
                    buttons={[
                      {
                        text: 'Go Back',
                        role: 'cancel',
                        cssClass: `modal backup-modal ${style['backup-modal']}`,
                        handler: onCloseBackupModal,
                      },
                      {
                        text: 'Continue',
                        handler: handleBackupLibrary,
                      },
                    ]}
                  />
                )}
                {currentStep === 3 && (
                  <div className={style['select-folder']}>
                    <div className="page-header">
                      <h2>Set Flattened Folder</h2>
                    </div>
                    <div>
                      <h4 style={{ textAlign: 'center' }}>
                        Pick a destination folder for your flattened files
                      </h4>
                      <button
                        className={'primary ' + style['select-folder-button']}
                        onClick={setDestinationButton}
                      >
                        <IonIcon className="ion-icon" icon={icons.folders} />{' '}
                        Select Folder
                      </button>
                    </div>
                  </div>
                )}
                {currentStep === 4 && destination && !isMovingFiles && (
                  <Verify
                    folders={selectedFolders}
                    destination={destination}
                    callback={handleVerify}
                  />
                )}
                {isMovingFiles && operationProgress && (
                  <div className={style['operation-progress']}>
                    <div className={style['operation-progress-inner']}>
                      <h2>Flattening Your Library…</h2>
                      <p className={style['operation-progress-subtitle']}>
                        Please don't close the app while files are being
                        processed.
                      </p>
                      <div className={style['progress-bar-track']}>
                        <div
                          className={style['progress-bar-fill']}
                          style={{ width: `${operationProgress.percentage}%` }}
                        />
                      </div>
                      <div className={style['progress-stats']}>
                        <span className={style['progress-percentage']}>
                          {operationProgress.percentage}%
                        </span>
                        <span className={style['progress-counts']}>
                          {operationProgress.completedCount.toLocaleString()} of{' '}
                          {operationProgress.totalFilesRequested.toLocaleString()}{' '}
                          files
                        </span>
                      </div>
                      {operationProgress.currentFile && (
                        <p className={style['progress-current-file']}>
                          {operationProgress.currentFile.split(/[\\/]/).pop()}
                        </p>
                      )}
                      <div className={style['cancel-container']}>
                        <button
                          className={`secondary ${style['cancel-button']}`}
                          onClick={handleCancelClick}
                        >
                          Cancel Operation
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {currentStep === 5 && fileOperationResponse && (
                  <Flatten
                    response={fileOperationResponse}
                    onFinish={handleFinish}
                  />
                )}

                <IonAlert
                  isOpen={showErrorAlert}
                  onDidDismiss={() => setShowErrorAlert(false)}
                  header="Invalid Destination Folder"
                  message={errorMessage}
                  buttons={['OK']}
                />

                <IonAlert
                  isOpen={showCancelAlert}
                  onDidDismiss={() => setShowCancelAlert(false)}
                  header="Cancel Operation?"
                  message="Are you sure you want to stop flattening? Files already processed will remain in their new locations."
                  buttons={[
                    {
                      text: 'No, Continue',
                      role: 'cancel',
                      handler: () => setShowCancelAlert(false),
                    },
                    {
                      text: 'Yes, Cancel',
                      cssClass: style['danger-button'],
                      handler: confirmCancel,
                    },
                  ]}
                />
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonContent>
    </IonPage>
  );
}
